import React, { useEffect, useState } from "react";
import { TextField, MenuItem, Button, Tooltip } from "@mui/material";
import MUIDataTable from "mui-datatables";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Box } from "@mui/material";

import { CloudUpload } from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import addregularizeicon from "../assets/images/addregularizeicon.png";
import editmodificationimg from "../assets/images/editmodificationimg.png";

// ← Import your API base URL here:
import API_BASE_URL from "../utilities/ApiConfig";

const RegularizeTableItCell = () => {
  const navigate = useNavigate();
  const { userDetails } = useSelector((state) => state.auth);

  // ─── Dialog open/close ───
  const [regularizeFormShow, setRegularizeFormShow] = useState(false);

  // ─── Master Lists ───
  const [districts, setDistricts] = useState([]);
  const [sros, setSros] = useState([]);
  const [staffForDistrict, setStaffForDistrict] = useState([]);
  const [staffForSro, setStaffForSro] = useState([]);

  // ─── Form State ───
  const [formData, setFormData] = useState({
    dcode: "",
    srocode: "",
    startDate: null,
    endDate: null,
    behalfOf: "",
    userRole: "",
    assignee: null,
    uploadLetter: null,
  });

  // Hard‐coded roles
  const roles = [
    { roleId: "Admin", roleName: "Admin" },
    { roleId: "Clerk", roleName: "Clerk" },
  ];

  // ─── All regularization records fetched from the API ───
  const [regularRequests, setRegularRequests] = useState([]);

  //
  // ── 1) On mount: fetch districts & all regularization records ──
  //
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const resp = await axios.get(
          "https://autoreg.eregistrationukgov.in/api/Auth/get_District_Mas"
        );
        setDistricts(resp.data.data);
      } catch (err) {
        console.error("Error fetching districts:", err);
      }
    };

    const fetchAllRegular = async () => {
      try {
        const resp = await axios.get(
          "https://autoreg.eregistrationukgov.in/api/Auth/sp_get_all_regularization"
        );
        setRegularRequests(resp.data.data);
      } catch (err) {
        console.error("Error fetching regularization records:", err);
      }
    };

    fetchDistricts();
    fetchAllRegular();
  }, []);

  //
  // ── 2) Whenever dcode changes → fetch that district’s SROs & district‐level staff ──
  //
  useEffect(() => {
    const fetchSrosAndDistrictStaff = async (selectedDistrict) => {
      if (!selectedDistrict) {
        setSros([]);
        setStaffForDistrict([]);
        return;
      }
      try {
        // 2.a) Fetch SROs
        const sroResp = await axios.get(
          `https://autoreg.eregistrationukgov.in/api/Auth/get_sro_master_data?dcode=${selectedDistrict}`
        );
        setSros(sroResp.data.data);

        // 2.b) Fetch district‐level staff (for “Name of Assignee”)
        const staffResp = await axios.get(
          `https://autoreg.eregistrationukgov.in/api/Auth/sp_get_dis_staff?dcode=${selectedDistrict}`
        );
        setStaffForDistrict(staffResp.data.data);
      } catch (err) {
        console.error("Error fetching SROs or district staff:", err);
      }
    };

    fetchSrosAndDistrictStaff(formData.dcode);

    // Reset SRO‐dependent fields
    setFormData((prev) => ({
      ...prev,
      srocode: "",
      behalfOf: "",
      assignee: null,
    }));
    setStaffForSro([]);
  }, [formData.dcode]);

  //
  // ── 3) Whenever srocode changes → fetch that SRO’s staff ──
  //
  useEffect(() => {
    const fetchSroStaff = async (district, selectedSro) => {
      if (!district || !selectedSro) {
        setStaffForSro([]);
        return;
      }
      try {
        const resp = await axios.get(
          `https://autoreg.eregistrationukgov.in/api/Auth/sp_get_sro_staff?dcode=${district}&srocode=${selectedSro}`
        );
        setStaffForSro(resp.data.data);
      } catch (err) {
        console.error("Error fetching SRO staff:", err);
      }
    };

    fetchSroStaff(formData.dcode, formData.srocode);

    // Reset “On Behalf Of” if SRO changes
    setFormData((prev) => ({
      ...prev,
      behalfOf: "",
    }));
  }, [formData.srocode, formData.dcode]);

  //
  // ── 4) Handle change on selects/textfields ──
  //
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  //
  // ── 5) File upload handler ──
  //
  const handleFileUpload = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFormData((prev) => ({
        ...prev,
        uploadLetter: e.target.files[0],
      }));
    }
  };

  //
  // ── 6) Submit new request ──
  //
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (
      !formData.dcode ||
      !formData.srocode ||
      !formData.startDate ||
      !formData.endDate ||
      !formData.behalfOf ||
      !formData.userRole ||
      !formData.assignee ||
      !formData.uploadLetter
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }

    const payload = new FormData();
    payload.append("Dcode", formData.dcode);
    payload.append("Srocode", formData.srocode);
    payload.append("OnBehalfOf", formData.behalfOf);
    payload.append("UserRole", formData.userRole);

    // Append the three fields from the “assignee” object
    payload.append("Id", formData.assignee.id);
    payload.append("OldUserId", formData.assignee.userid);
    payload.append("Username", formData.assignee.username);

    payload.append(
      "StartDate",
      dayjs(formData.startDate).format("YYYY-MM-DDTHH:mm:ss")
    );
    payload.append(
      "EndDate",
      dayjs(formData.endDate).format("YYYY-MM-DDTHH:mm:ss")
    );

    payload.append("AttachmentFile", formData.uploadLetter);

    if (userDetails?.login_id) {
      payload.append("RequestedBy", userDetails.login_id);
    }

    try {
      await axios.post(
        "https://autoreg.eregistrationukgov.in/api/Auth/insert_regularization_request",
        payload,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      toast.success("Regularization request submitted successfully.");
      setRegularizeFormShow(false);

      // Re-fetch all regularization records so the table updates
      const resp = await axios.get(
        "https://autoreg.eregistrationukgov.in/api/Auth/sp_get_all_regularization"
      );
      setRegularRequests(resp.data.data);
    } catch (err) {
      console.error("Error submitting request:", err);
      toast.error("Failed to submit request. Please try again.");
    }
  };

  //
  // ── 7) Cancel ──
  //
  const handleCancel = () => {
    setRegularizeFormShow(false);
  };

  //
  // ── 8) “New Request” toolbar button ──
  //
  const handleNavigate = () => {
    setRegularizeFormShow(true);
  };

  //
  // ── 9) “Edit” button logic in table ──
  //
  const handleNavigateUpdatePages = (typeRecpt, regYear, vol, sr) => {
    let targetPage = "";
    const lower = typeRecpt.toLowerCase();
    if (lower === "search") {
      targetPage = "/SearchReceipt";
    } else if (lower === "copy") {
      targetPage = "/CopyReceipt";
    } else if (lower === "inspection") {
      targetPage = "/InspectionReceipt";
    } else if (lower === "commission") {
      targetPage = "/Commision";
    } else if (lower === "defeceit") {
      targetPage = "/DeficiateReceipt";
    } else if (lower === "others") {
      targetPage = "/Other";
    } else if (lower === "miscelleneous") {
      targetPage = "/NewReceipt";
    } else {
      toast.error("Invalid receipt type");
      return;
    }

    navigate(targetPage, {
      state: { regYear, vol, sr, typeRecpt, mod: true },
    });
  };

  //
  // ── 10) Helper: extract filename from full path ──
  //
  const extractFileName = (path) => {
    if (!path) return "";
    // Splits on both / and \\, then returns last segment
    const parts = path.split(/[\\/]/);
    return parts[parts.length - 1];
  };

  //
  // ── 11) Prepare “TableData” from `regularRequests` ──
  //
  const TableData = regularRequests.map((item, index) => ({
    sn: index + 1,
    requestId: item.requestId,
    id: item.id,
    dcode: item.dcode,
    srocode: item.srocode,
    district: item.district, // if your API provides it
    sro: item.sro, // if your API provides it
    username: item.username,
    userid: item.userid,
    thumb: item.thumb,
    levelId: item.levelId,
    status: item.status,
    userRole: item.userRole,
    userDesig: item.userDesig,
    email: item.email,
    mobileNo: item.mobileNo,
    onBehalfOf: item.onBehalfOf,
    assignedTo: item.assignedTo,
    attachment: item.attachment || "", // full path (e.g. "wwwroot\\attachments\\…")
    requestedOn: item.requestedOn,
    approvedBy: item.approvedBy,
    requestedBy: item.requestedBy,
    approvedOn: item.approvedOn,
    startDate: item.startDate,
    endDate: item.endDate,
    closedOn: item.closedOn,
    recordStatus: item.recordStatus,
    dateRange: `${dayjs(item.startDate).format("DD/MM/YYYY")} - ${dayjs(
      item.endDate
    ).format("DD/MM/YYYY")}`,
  }));

  //
  // ── 12) Define columns for MUIDataTable ──
  //
  const columns = [
    {
      name: "username",
      label: "Name of Assignee",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <div className="text-start text-xs md:text-sm">{value}</div>
        ),
      },
    },
    {
      name: "dateRange",
      label: "From date – To date",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <div className="text-start text-xs md:text-sm">{value}</div>
        ),
      },
    },
    // If you want to display district name instead of code, uncomment:
    // {
    //   name: "district",
    //   label: "Assigned District",
    //   options: {
    //     filter: false,
    //     sort: true,
    //     customBodyRender: (value) => (
    //       <div className="text-start text-xs md:text-sm">{value}</div>
    //     ),
    //   },
    // },
    {
      name: "sro",
      label: "Assigned SRO",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <div className="text-start text-xs md:text-sm">{value}</div>
        ),
      },
    },
    {
      name: "userRole",
      label: "Assigned Role",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <div className="text-start text-xs md:text-sm">{value}</div>
        ),
      },
    },
    {
      name: "attachment",
      label: "Document",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          if (!value) {
            return (
              <span className="text-gray-400 text-xs md:text-sm">No file</span>
            );
          }
          const fileName = extractFileName(value);
          return (
            <a
              href={`https://autoreg.eregistrationukgov.in/api/Files/view/${encodeURIComponent(
                fileName
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#39bbf7] hover:text-[#3383a8] underline text-xs"
            >
              View
            </a>
          );
        },
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <div
            className={`text-xs md:text-sm ${
              value === "Open" ? "text-[#DD0808]" : "text-[#219B03]"
            }`}
          >
            {value}
          </div>
        ),
      },
    },

    // If you need an “Action” column to edit, you can re-enable:
    // {
    //   name: "requestId",
    //   label: "ACTION",
    //   options: {
    //     filter: false,
    //     sort: false,
    //     customBodyRender: (_value, tableMeta) => {
    //       const rowData = regularRequests[tableMeta.rowIndex];
    //       return (
    //         <button
    //           className="capitalize py-1 px-2 rounded-md cursor-pointer"
    //           onClick={() =>
    //             handleNavigateUpdatePages(
    //               rowData.typeRecpt,
    //               rowData.regYear,
    //               rowData.vol,
    //               rowData.sr
    //             )
    //           }
    //         >
    //           <img
    //             src={editmodificationimg}
    //             alt="Edit"
    //             className="w-6 h-6"
    //           />
    //         </button>
    //       );
    //     },
    //   },
    // },
  ];

  const options = {
    selectableRows: "none",
    filterType: "dropdown",
    elevation: 0,
    rowsPerPageOptions: [10, 15, 25, 50],
    responsive: "standard",
    tableBodyHeight: "auto",
    tableBodyMaxHeight: "auto",
    download: false,
    print: false,
    viewColumns: false,
    search: true,
    filter: false,
    customToolbar: () => (
      <>
        {userDetails?.userRole !== "SuperAdmin" && (
          <Tooltip title="Add New">
            <button
              onClick={handleNavigate}
              className="bg-blue-400 text-white text-sm px-3 py-2 rounded hover:bg-blue-700 focus:outline-none inline-flex items-center gap-2"
            >
              <img src={addregularizeicon} alt="Add New" className="h-5 w-5" />
              New Request
            </button>
          </Tooltip>
        )}
      </>
    ),
  };

  const getMuiTheme = () =>
    createTheme({
      typography: {
        fontFamily: "Poppins",
      },
      palette: {
        background: {
          paper: "#ffffff",
          default: "#ffffff",
        },
        mode: "light",
      },
      components: {
        MuiTableCell: {
          styleOverrides: {
            head: {
              backgroundColor: "#ffffff !important",
              color: "#929292 !important",
              padding: "2px 1px",
              borderBottom: "2px solid #ECECEC",
              borderRight: "0px solid #ddd",
              fontSize: "10px",
              textAlign: "start",
              "@media (min-width: 768px)": {
                fontSize: "inherit",
              },
              "& .MuiTableSortLabel-root": {
                color: "#000000 !important",
                "&.MuiTableSortLabel-active": {
                  color: "#000000 !important",
                },
              },
              "& .MuiTableSortLabel-icon": {
                color: "#000000 !important",
              },
              "&:hover": {
                color: "#ffffff !important",
                backgroundColor: "#929292 !important",
              },
            },
            body: {
              padding: "7px 15px",
              color: "#000000",
              borderBottom: "0px solid #ECECEC",
              borderRight: "0px solid #000000",
            },
            footer: {
              padding: "7px 15px",
              borderBottom: "0px solid #ECECEC",
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              backgroundColor: "#F1F1F1",
              borderRadius: "1px",
              padding: "1px 5px",
              color: "#6A6A6A !important",
              borderBottom: "0px",
            },
          },
        },
      },
    });

  return (
    <>
      {/* ─── New Request Dialog ─── */}
      {regularizeFormShow && (
        <div className="fixed inset-0 flex items-center justify-center bg-[#000000] bg-opacity-60 z-50 md:p-0 p-2">
          <form
            onSubmit={handleSubmit}
            className="p-6 w-full md:w-1/2 mx-auto bg-white rounded-md shadow-md"
          >
            <h2 className="text-xl font-semibold mb-4">
              New Regularization Request
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 text-[#000000]">
              {/* ─── Select District ─── */}
              <div className="col-span-2 relative">
                <TextField
                  select
                  name="dcode"
                  label="Select District"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={formData.dcode}
                  onChange={handleChange}
                  required
                >
                  {districts.map((dist) => (
                    <MenuItem key={dist.dcode} value={dist.dcode}>
                      {dist.districtNameEng}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              {/* ─── Select SRO ─── */}
              <div className="col-span-2 relative">
                <TextField
                  select
                  name="srocode"
                  label="Select SRO"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={formData.srocode}
                  onChange={handleChange}
                  required
                  disabled={!formData.dcode}
                >
                  {sros.map((sro) => (
                    <MenuItem key={sro.srocode} value={sro.srocode}>
                      {sro.sroname}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              {/* ─── From Date ─── */}
              {/* <div className="col-span-1 relative">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="From Date"
                    value={formData.startDate}
                    onChange={(newValue) =>
                      setFormData((prev) => ({
                        ...prev,
                        startDate: newValue,
                      }))
                    }
                    slotProps={{ textField: { size: "small" } }}
                    required
                  />
                </LocalizationProvider>
              </div> */}
              <div className="col-span-1 relative">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="From Date"
                    value={formData.startDate}
                    onChange={(newValue) =>
                      setFormData((prev) => ({
                        ...prev,
                        startDate: newValue,
                      }))
                    }
                    slotProps={{ textField: { size: "small" } }}
                    required
                    maxDate={dayjs()} // disables all dates after today
                  />
                </LocalizationProvider>
              </div>

              {/* ─── To Date ─── */}
              {/* <div className="col-span-1 relative">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="To Date"
                    value={formData.endDate}
                    onChange={(newValue) =>
                      setFormData((prev) => ({
                        ...prev,
                        endDate: newValue,
                      }))
                    }
                    slotProps={{ textField: { size: "small" } }}
                    required
                  />
                </LocalizationProvider>
              </div> */}

              <div className="col-span-1 relative">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="To Date"
                    value={formData.endDate}
                    onChange={(newValue) =>
                      setFormData((prev) => ({
                        ...prev,
                        endDate: newValue,
                      }))
                    }
                    slotProps={{ textField: { size: "small" } }}
                    required
                    minDate={dayjs()} // disables all dates before today
                  />
                </LocalizationProvider>
              </div>
              {/* ─── On Behalf Of ─── */}
              <div className="col-span-2 relative">
                <TextField
                  select
                  name="behalfOf"
                  label="On Behalf Of"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={formData.behalfOf}
                  onChange={handleChange}
                  required
                  disabled={!formData.srocode}
                >
                  {staffForSro.map((st) => (
                    <MenuItem key={st.id} value={st.id}>
                      {st.username}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              {/* ─── Assigned Role ─── */}
              <div className="col-span-2 relative">
                <TextField
                  select
                  name="userRole"
                  label="Assigned Role"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={formData.userRole}
                  onChange={handleChange}
                  required
                >
                  {roles.map((role) => (
                    <MenuItem key={role.roleId} value={role.roleId}>
                      {role.roleName}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              {/* ─── Name of Assignee ─── */}
              <div className="col-span-2 relative">
                <TextField
                  select
                  label="Name of Assignee"
                  name="assignee"
                  size="small"
                  fullWidth
                  variant="outlined"
                  value={formData.assignee?.id || ""}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const found =
                      staffForDistrict.find((a) => a.id === selectedId) || null;
                    setFormData((prev) => ({
                      ...prev,
                      assignee: found,
                    }));
                  }}
                  required
                  disabled={!formData.dcode}
                >
                  {staffForDistrict.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.username}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              {/* ─── Upload Letter ─── */}
              <div className="col-span-2 relative border-dashed bg-[#F3F9FF] rounded-md border-2 border-[#4F9CF9] p-1 flex justify-between items-center">
                <span className="text-gray-500">Upload Letter</span>
                <input
                  type="file"
                  id="upload"
                  hidden
                  onChange={handleFileUpload}
                  accept=".pdf,.jpg,.png"
                  required
                />
                <label htmlFor="upload">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<CloudUpload />}
                    size="small"
                    sx={{ height: "34px", padding: "4px" }}
                  >
                    Upload
                  </Button>
                </label>
              </div>

              {/* ─── Send / Cancel Buttons ─── */}
              <div className="col-span-1 relative md:col-end-3">
                <button
                  type="submit"
                  className="w-full bg-[#4F9CF9] text-white px-3 md:px-6 py-2 rounded hover:bg-[#3e85db] focus:outline-none"
                >
                  Send Request
                </button>
              </div>
              <div className="col-span-1 relative md:col-end-4">
                <button
                  type="button"
                  className="w-full text-[#DA0000] px-3 md:px-6 py-2 border border-[#DA0000] rounded hover:bg-[#ca3d3d] hover:text-[#ffffff] focus:outline-none"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ─── DataTable ─── */}
      <div className="py-8 w-full overflow-x-auto">
        {/* 
    inline-block so min-w actually takes effect,
    pick your own min-w (e.g. 600px or 640px)
  */}
        <div className="inline-block min-w-[600px] md:w-full">
          <Box width="100%">
            <ThemeProvider theme={getMuiTheme()}>
              <MUIDataTable
                title={"Power Regularization"}
                data={TableData}
                columns={columns}
                options={options}
              />
            </ThemeProvider>
          </Box>
        </div>
      </div>
    </>
  );
};

export default RegularizeTableItCell;
